/**
 * Action Notification Component
 * 
 * Dikkat çekici, animasyonlu pop-up bildirimler
 * Ürün düzenleme, sipariş, stok uyarıları gibi işlemler için
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import './ActionNotification.css';

// Bildirim ID counter
let notificationId = 0;

// Global notification state (React dışından erişim için)
let globalAddNotification = null;

/**
 * Bildirim göstermek için global fonksiyon
 */
export const showActionNotification = (options) => {
    if (globalAddNotification) {
        return globalAddNotification(options);
    }
    console.warn('ActionNotification provider not mounted');
    return null;
};

/**
 * Kısayol fonksiyonlar
 */
export const notify = {
    success: (message, options = {}) => showActionNotification({
        type: 'success',
        message,
        title: options.title || 'Başarılı',
        ...options
    }),
    error: (message, options = {}) => showActionNotification({
        type: 'error',
        message,
        title: options.title || 'Hata',
        duration: 8000,
        ...options
    }),
    warning: (message, options = {}) => showActionNotification({
        type: 'warning',
        message,
        title: options.title || 'Uyarı',
        ...options
    }),
    info: (message, options = {}) => showActionNotification({
        type: 'info',
        message,
        title: options.title || 'Bilgi',
        ...options
    }),
    order: (orderData) => showActionNotification({
        type: 'order',
        title: '🎉 Yeni Sipariş!',
        message: `${orderData.customer || 'Müşteri'} - ${orderData.total?.toLocaleString('tr-TR') || 0} TL`,
        subtitle: orderData.platform || 'Pazaryeri',
        icon: 'shopping-cart',
        sound: true,
        duration: 10000,
        action: orderData.action
    }),
    stock: (productName, currentStock) => showActionNotification({
        type: 'warning',
        title: '⚠️ Düşük Stok Uyarısı',
        message: `${productName}`,
        subtitle: `Mevcut stok: ${currentStock}`,
        icon: 'box',
        duration: 12000
    }),
    sync: (message, success = true) => showActionNotification({
        type: success ? 'success' : 'error',
        title: success ? '✅ Senkronizasyon Tamamlandı' : '❌ Senkronizasyon Hatası',
        message,
        icon: 'sync',
        duration: 6000
    }),
    product: (action, productName) => showActionNotification({
        type: 'success',
        title: action === 'create' ? '✨ Ürün Oluşturuldu' :
            action === 'update' ? '📝 Ürün Güncellendi' :
                action === 'delete' ? '🗑️ Ürün Silindi' : 'Ürün İşlemi',
        message: productName,
        icon: 'cube',
        duration: 5000
    })
};

/**
 * Tek bildirim komponenti
 */
const NotificationItem = ({ notification, onDismiss, onAction }) => {
    const [isExiting, setIsExiting] = useState(false);
    const [progress, setProgress] = useState(100);
    const timerRef = useRef(null);
    const startTimeRef = useRef(Date.now());

    const handleDismiss = useCallback(() => {
        setIsExiting(true);
        setTimeout(() => onDismiss(notification.id), 300);
    }, [notification.id, onDismiss]);

    useEffect(() => {
        if (notification.duration > 0) {
            const duration = notification.duration;
            const interval = 50;

            timerRef.current = setInterval(() => {
                const elapsed = Date.now() - startTimeRef.current;
                const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
                setProgress(remaining);

                if (remaining <= 0) {
                    clearInterval(timerRef.current);
                    handleDismiss();
                }
            }, interval);

            return () => {
                if (timerRef.current) {
                    clearInterval(timerRef.current);
                }
            };
        }
    }, [notification.duration, handleDismiss]);

    // Pause on hover
    const handleMouseEnter = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
    };

    const handleMouseLeave = () => {
        if (notification.duration > 0) {
            startTimeRef.current = Date.now() - ((100 - progress) / 100) * notification.duration;
            const duration = notification.duration;
            const interval = 50;

            timerRef.current = setInterval(() => {
                const elapsed = Date.now() - startTimeRef.current;
                const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
                setProgress(remaining);

                if (remaining <= 0) {
                    clearInterval(timerRef.current);
                    handleDismiss();
                }
            }, interval);
        }
    };

    const getIcon = () => {
        if (notification.icon) {
            return <i className={`fas fa-${notification.icon}`}></i>;
        }
        switch (notification.type) {
            case 'success': return <i className="fas fa-check"></i>;
            case 'error': return <i className="fas fa-times"></i>;
            case 'warning': return <i className="fas fa-exclamation"></i>;
            case 'order': return <i className="fas fa-shopping-cart"></i>;
            default: return <i className="fas fa-info"></i>;
        }
    };

    const handleActionClick = () => {
        if (notification.action?.onClick) {
            notification.action.onClick();
        }
        handleDismiss();
    };

    return (
        <div
            className={`action-notification action-notification-${notification.type} ${isExiting ? 'exiting' : ''}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            role="alert"
        >
            {/* Animated background */}
            <div className="notification-bg-effect"></div>

            {/* Icon */}
            <div className="notification-icon-wrapper">
                <div className="notification-icon">
                    {getIcon()}
                </div>
                <div className="notification-icon-pulse"></div>
            </div>

            {/* Content */}
            <div className="notification-content">
                {notification.subtitle && (
                    <span className="notification-subtitle">{notification.subtitle}</span>
                )}
                <h4 className="notification-title">{notification.title}</h4>
                <p className="notification-message">{notification.message}</p>

                {/* Action button */}
                {notification.action && (
                    <button
                        className="notification-action-btn"
                        onClick={handleActionClick}
                    >
                        {notification.action.label || 'Görüntüle'}
                        <i className="fas fa-arrow-right"></i>
                    </button>
                )}
            </div>

            {/* Close button */}
            <button
                className="notification-close"
                onClick={handleDismiss}
                aria-label="Bildirimi kapat"
            >
                <i className="fas fa-times"></i>
            </button>

            {/* Progress bar */}
            {notification.duration > 0 && (
                <div
                    className="notification-progress"
                    style={{ width: `${progress}%` }}
                />
            )}
        </div>
    );
};

/**
 * Ana Provider Component
 */
export const ActionNotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const audioRef = useRef(null);

    const addNotification = useCallback((options) => {
        const id = ++notificationId;
        const notification = {
            id,
            type: options.type || 'info',
            title: options.title || '',
            message: options.message || '',
            subtitle: options.subtitle,
            icon: options.icon,
            duration: options.duration ?? 5000,
            sound: options.sound || false,
            action: options.action
        };

        setNotifications(prev => [...prev, notification]);

        // Play sound for important notifications
        if (notification.sound && audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => { });
        }

        return id;
    }, []);

    const removeNotification = useCallback((id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    // Register global function
    useEffect(() => {
        globalAddNotification = addNotification;
        return () => {
            globalAddNotification = null;
        };
    }, [addNotification]);

    return (
        <>
            {children}

            {/* Notification sound */}
            <audio ref={audioRef} preload="auto">
                <source src="/sounds/notification.mp3" type="audio/mpeg" />
            </audio>

            {/* Notifications portal */}
            {createPortal(
                <div className="action-notification-container" aria-live="polite">
                    {notifications.map(notification => (
                        <NotificationItem
                            key={notification.id}
                            notification={notification}
                            onDismiss={removeNotification}
                        />
                    ))}
                </div>,
                document.body
            )}
        </>
    );
};

/**
 * Hook for using notifications
 */
export const useActionNotification = () => {
    return {
        show: showActionNotification,
        ...notify
    };
};

export default ActionNotificationProvider;
